import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Tooltip, Typography, Spin, Alert } from 'antd';
import { HeartOutlined, FireOutlined, StockOutlined, UserOutlined, DashboardOutlined } from '@ant-design/icons';
import moment from 'moment';
import { getLatestVitals, formatVitalsForBanner } from '../services/vitalsService';
import { isVitalsValueNormal } from '../utils/vitalsDisplayMapping';
import { SafeSpan } from './SafeText';

const { Text } = Typography;

/**
 * VitalsBanner Component
 * Displays the patient's most recent vital signs in a compact horizontal layout
 * @param {string} patientId - The patient ID
 * @param {boolean} compact - Whether to render in compact inline mode
 * @param {number} refreshTrigger - Trigger to refresh vitals data when incremented
 */
const VitalsBanner = ({ patientId, compact = false, refreshTrigger = 0 }) => {
  const [vitalsData, setVitalsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Helper function to safely extract value from potential object
  const getVitalValue = (vital) => {
    // Check if it's a non-null object first
    if (vital && typeof vital === 'object') {
      // If it's an object, ONLY return vital.value if it exists and is not undefined
      let extractedValue = vital.value !== undefined ? vital.value : undefined;

      // Additional check: if the extracted value is still an object, try to extract its value
      if (extractedValue && typeof extractedValue === 'object' && extractedValue.value !== undefined) {
        extractedValue = extractedValue.value;
      }

      // If extractedValue is still an object, check if it only has unit and no actual value
      if (extractedValue && typeof extractedValue === 'object') {
        // If the object only has unit property (like {unit: "kg"}), return null
        if (extractedValue.unit && Object.keys(extractedValue).length === 1) {
          return null;
        }
        // If it has a value property, try to extract that
        if (extractedValue.value !== undefined) {
          return extractedValue.value;
        }
        // Otherwise, return null to indicate no usable value
        return null;
      }

      // If we still don't have a valid value, check if the object only has unit
      if (extractedValue === undefined) {
        // If object only has unit property and no value, return null to indicate no data
        if (vital.unit && Object.keys(vital).length === 1) {
          return null;
        }
        // For other cases where we can't extract a value, return null
        return null;
      }

      return extractedValue;
    }
    // If it's not an object (e.g., null, undefined, number, string), return it directly
    return vital;
  };

  // Helper function to get text color based on normal range
  const getValueTextColor = (vital, parameterKey) => {
    const extractedValue = getVitalValue(vital);
    if (!vital || extractedValue === null || extractedValue === undefined) {
      return '#000000'; // Black for missing values
    }

    // For blood pressure, we need to check both systolic and diastolic
    if (parameterKey === 'bloodPressure' && vital && vital.value) {
      // Blood pressure value is in format "120/80" - extract systolic and diastolic
      const bpParts = vital.value.split('/');
      if (bpParts.length === 2) {
        const systolicValue = parseFloat(bpParts[0]);
        const diastolicValue = parseFloat(bpParts[1]);

        if (!isNaN(systolicValue) && !isNaN(diastolicValue)) {
          const systolicNormal = isVitalsValueNormal('vitals_sbp', systolicValue);
          const diastolicNormal = isVitalsValueNormal('vitals_dbp', diastolicValue);

          // If either is abnormal, show as abnormal
          if (systolicNormal === false || diastolicNormal === false) {
            return '#ff4d4f'; // Red for abnormal values
          }
          return '#000000'; // Black for normal values
        }
      }
      // If we can't parse the blood pressure, return default color
      return '#000000';
    }

    // Map vital types to parameter keys
    const parameterKeyMap = {
      heartRate: 'vitals_rhr',
      weight: 'vitals_weight',
      bmi: 'vitals_bmi',
      temperature: 'vitals_temp_c'
    };

    const vitalParamKey = parameterKeyMap[parameterKey];
    if (vitalParamKey) {
      const isNormal = isVitalsValueNormal(vitalParamKey, extractedValue);
      if (isNormal === false) {
        return '#ff4d4f'; // Red for abnormal values
      }
    }

    return '#000000'; // Black for normal or unknown values
  };

  useEffect(() => {
    const fetchVitals = async () => {
      if (!patientId) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        const response = await getLatestVitals(patientId);
        const formattedData = formatVitalsForBanner(response);
        setVitalsData(formattedData);
      } catch (err) {
        console.error('Error fetching vitals for banner:', err);
        setError('Failed to load vital signs');
      } finally {
        setLoading(false);
      }
    };

    fetchVitals();
  }, [patientId, refreshTrigger]);

  // Helper function to safely extract units from vital object
  const getVitalUnits = (vital) => {
    if (!vital || typeof vital !== 'object') return '';

    let units = vital.units;
    // If units is an object, try to extract its value
    if (units && typeof units === 'object' && units.value !== undefined) {
      units = units.value;
    }

    return units || '';
  };

  // Helper function to render individual vital sign
  const renderVitalSign = (vital, label, icon, color = '#1890ff', compact = false, parameterKey = null) => {
    const extractedValue = getVitalValue(vital);
    const extractedUnits = getVitalUnits(vital);

    // Additional safety check to ensure extractedValue is not an object
    if (typeof extractedValue === 'object' && extractedValue !== null) {
      console.error('🚨 VitalsBanner: extractedValue is still an object!', {
        label,
        parameterKey,
        vital,
        extractedValue
      });

      // Try to extract value from the object if it has a value property
      let safeValue = extractedValue.value !== undefined ? extractedValue.value : null;

      // If still no safe value and object only has unit, treat as missing data
      if (safeValue === null && extractedValue.unit && Object.keys(extractedValue).length === 1) {
        console.warn('🔧 VitalsBanner: Object only has unit, treating as missing data');
        return null; // Don't render anything for missing data
      }

      // As last resort, convert to string
      if (safeValue === null) {
        safeValue = JSON.stringify(extractedValue);
      }

      console.warn('🔧 VitalsBanner: Using safe value:', safeValue);
      return renderVitalSign({ value: safeValue, units: extractedUnits, timestamp: vital?.timestamp }, label, icon, color, compact, parameterKey);
    }

    // Additional safety check to ensure extractedUnits is not an object
    if (typeof extractedUnits === 'object' && extractedUnits !== null) {
      console.error('🚨 VitalsBanner: extractedUnits is still an object!', {
        label,
        parameterKey,
        vital,
        extractedUnits
      });
    }

    // Ensure both value and units are safe for rendering
    let safeValue = typeof extractedValue === 'object' && extractedValue !== null ?
      JSON.stringify(extractedValue) : extractedValue;
    const safeUnits = typeof extractedUnits === 'object' && extractedUnits !== null ?
      JSON.stringify(extractedUnits) : extractedUnits;

    // Convert temperature to Celsius if it appears to be in Fahrenheit
    if (parameterKey === 'temperature' && safeValue !== null && safeValue !== undefined) {
      const tempValue = parseFloat(safeValue);
      if (!isNaN(tempValue)) {
        // If temperature is > 50, assume it's in Fahrenheit and convert to Celsius
        if (tempValue > 50) {
          safeValue = ((tempValue - 32) * 5/9).toFixed(1);
        } else {
          // Already in Celsius, just format to 1 decimal place
          safeValue = tempValue.toFixed(1);
        }
      }
    }

    if (!vital || safeValue === null || safeValue === undefined) {
      if (compact) {
        return (
          <SafeSpan style={{ color: '#bfbfbf', fontSize: '12px', marginRight: '12px' }}>
            {icon} N/A
          </SafeSpan>
        );
      }
      return (
        <div style={{ textAlign: 'center', padding: '8px' }}>
          <div style={{ color: '#bfbfbf', fontSize: '16px', marginBottom: '4px' }}>
            {icon}
          </div>
          <Text type="secondary" style={{ fontSize: '12px', display: 'block' }}>
            {label}
          </Text>
          <Text type="secondary" style={{ fontSize: '11px' }}>
            N/A
          </Text>
        </div>
      );
    }

    const tooltipContent = vital.timestamp
      ? `Recorded: ${moment(vital.timestamp).format('MM/DD/YYYY HH:mm A')}`
      : 'No timestamp available';

    if (compact) {
      const textColor = getValueTextColor(vital, parameterKey);
      return (
        <Tooltip title={tooltipContent} placement="bottom" key={label}>
          <SafeSpan
            style={{
              fontSize: '12px',
              marginRight: '12px',
              cursor: 'pointer',
              fontWeight: '500'
            }}
          >
            <SafeSpan style={{ color: color }}>{icon}</SafeSpan>{' '}
            <SafeSpan style={{ color: textColor }}>
              {safeValue}{safeUnits ? ` ${safeUnits}` : ''}
            </SafeSpan>
          </SafeSpan>
        </Tooltip>
      );
    }

    return (
      <Tooltip title={tooltipContent} placement="bottom">
        <div
          style={{
            textAlign: 'center',
            padding: '8px',
            cursor: 'pointer',
            borderRadius: '6px',
            transition: 'background-color 0.2s ease'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = '#f5f5f5';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'transparent';
          }}
        >
          <div style={{ color, fontSize: '16px', marginBottom: '4px' }}>
            {icon}
          </div>
          <Text strong style={{ fontSize: '12px', display: 'block', color: '#262626' }}>
            {label}
          </Text>
          <Text style={{ fontSize: '13px', fontWeight: '600', color: getValueTextColor(vital, parameterKey) }}>
            {safeValue}{safeUnits ? ` ${safeUnits}` : ''}
          </Text>
        </div>
      </Tooltip>
    );
  };

  if (loading) {
    if (compact) {
      return <Spin size="small" style={{ marginLeft: '8px' }} />;
    }
    return (
      <Card
        size="small"
        style={{
          marginBottom: '16px',
          background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
          border: '1px solid #bae7ff'
        }}
      >
        <div style={{ textAlign: 'center', padding: '20px' }}>
          <Spin size="small" />
          <Text type="secondary" style={{ marginLeft: '8px' }}>Loading vitals...</Text>
        </div>
      </Card>
    );
  }

  if (error) {
    if (compact) {
      return <Text type="secondary" style={{ fontSize: '12px', marginLeft: '8px' }}>Vitals unavailable</Text>;
    }
    return (
      <Alert
        message={error}
        type="warning"
        size="small"
        style={{ marginBottom: '16px' }}
        showIcon
      />
    );
  }

  // Compact inline rendering
  if (compact) {
    return (
      <SafeSpan style={{ marginLeft: '16px', fontSize: '12px' }}>
        {renderVitalSign(vitalsData?.bloodPressure, 'BP', <HeartOutlined />, '#ff4d4f', true, 'bloodPressure')}
        {renderVitalSign(vitalsData?.heartRate, 'HR', <DashboardOutlined />, '#52c41a', true, 'heartRate')}
        {renderVitalSign(vitalsData?.weight, 'Wt', <StockOutlined />, '#722ed1', true, 'weight')}
        {renderVitalSign(vitalsData?.bmi, 'BMI', <UserOutlined />, '#fa8c16', true, 'bmi')}
        {renderVitalSign(vitalsData?.temperature, 'Temp', <FireOutlined />, '#1890ff', true, 'temperature')}
      </SafeSpan>
    );
  }

  return (
    <Card
      size="small"
      title={
        <Text strong style={{ color: '#1890ff', fontSize: '14px' }}>
          📊 Latest Vital Signs
        </Text>
      }
      style={{
        marginBottom: '16px',
        background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
        border: '1px solid #bae7ff',
        boxShadow: '0 2px 8px rgba(24, 144, 255, 0.1)'
      }}
      bodyStyle={{ padding: '12px 16px' }}
    >
      <Row gutter={[16, 0]} justify="space-around" align="middle">
        <Col xs={24} sm={24} md={4} lg={4} xl={4}>
          {renderVitalSign(
            vitalsData?.bloodPressure,
            'Blood Pressure',
            <HeartOutlined />,
            '#ff4d4f',
            false,
            'bloodPressure'
          )}
        </Col>

        <Col xs={24} sm={24} md={4} lg={4} xl={4}>
          {renderVitalSign(
            vitalsData?.heartRate,
            'Heart Rate',
            <DashboardOutlined />,
            '#52c41a',
            false,
            'heartRate'
          )}
        </Col>

        <Col xs={24} sm={24} md={4} lg={4} xl={4}>
          {renderVitalSign(
            vitalsData?.weight,
            'Weight',
            <StockOutlined />,
            '#722ed1',
            false,
            'weight'
          )}
        </Col>

        <Col xs={24} sm={24} md={4} lg={4} xl={4}>
          {renderVitalSign(
            vitalsData?.bmi,
            'BMI',
            <UserOutlined />,
            '#fa8c16',
            false,
            'bmi'
          )}
        </Col>

        <Col xs={24} sm={24} md={4} lg={4} xl={4}>
          {renderVitalSign(
            vitalsData?.temperature,
            'Temperature',
            <FireOutlined />,
            '#1890ff',
            false,
            'temperature'
          )}
        </Col>
      </Row>
    </Card>
  );
};

export default VitalsBanner;
